import type { MinimarkNode } from '@nuxt/content'

type CategoryStyle = {
  /** Vuetify theme color for chips. */
  color: string
  /** Hex used to anchor the AbstractImage palette. */
  bg: string
  /** AbstractImage generative vocabulary for this category family. */
  variant: 'mesh' | 'lowpoly' | 'bauhaus'
}

const CATEGORY_DEFAULT: CategoryStyle = { color: 'grey', bg: '#455A64', variant: 'mesh' }

// Single source of truth for per-category presentation. Synonymous category
// names share a style so they read as one visual family.
const CATEGORY_STYLES: Record<string, CategoryStyle> = {
  'Heart & Theology': { color: 'primary', bg: '#6A1B9A', variant: 'mesh' },
  'Heart & Struggle': { color: 'primary', bg: '#6A1B9A', variant: 'mesh' },
  'Song Selection & Theology': { color: 'info', bg: '#1565C0', variant: 'mesh' },
  'Leadership & Team': { color: 'warning', bg: '#EF6C00', variant: 'bauhaus' },
  'Team Management': { color: 'warning', bg: '#EF6C00', variant: 'bauhaus' },
  'Practical Tools': { color: 'success', bg: '#2E7D32', variant: 'lowpoly' },
  'Tech & Tips': { color: 'success', bg: '#2E7D32', variant: 'lowpoly' },
  'Practical Ministry': { color: 'success', bg: '#2E7D32', variant: 'lowpoly' },
}

const categoryStyle = (category?: string): CategoryStyle => (category && CATEGORY_STYLES[category]) || CATEGORY_DEFAULT

export const articleCategoryColor = (category?: string): string => categoryStyle(category).color

export const articleCategoryBg = (category?: string): string => categoryStyle(category).bg

/**
 * Maps a category to an AbstractImage generative vocabulary, so each category
 * reads as a visually distinct family. Articles stay unique within the family
 * because the generator is seeded on the article title.
 */
export const articleCategoryVariant = (category?: string): CategoryStyle['variant'] => categoryStyle(category).variant

export const articleReadingTime = (content: string | undefined): string => {
  if (!content) return '1 min read'
  const wordsPerMinute = 200
  const wordCount = content.split(/\s+/).length
  const minutes = Math.ceil(wordCount / wordsPerMinute)
  return `${minutes} min read`
}

// Nuxt Content (minimark) nodes are `[tag, props, ...children]` — children
// start at index 2 and a paragraph with inline bold/code/links has several.
const nodeText = (node: MinimarkNode): string => {
  if (typeof node === 'string') return node
  const [, , ...children] = node
  return children.map(nodeText).join('')
}

/** The slice of a Nuxt Content page item these helpers read. */
type ArticleBody = { body?: { value?: MinimarkNode[] | string } } | null | undefined

export const articleBodyToString = (article: ArticleBody): string => {
  const value = article?.body?.value
  if (!value) return ''
  if (typeof value === 'string') return value
  return value.map(nodeText).join(' ')
}

export const articleFirstParagraph = (article: ArticleBody): string => {
  const value = article?.body?.value
  if (!Array.isArray(value)) return ''
  for (const node of value) {
    if (typeof node === 'string' || node[0] !== 'p') continue
    const text = nodeText(node).trim()
    if (text) return text
  }
  return ''
}
